import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { SuperHero, BrewGuidesPreview } from "@bluebottlecoffee/design-system";
import sanityClient from "../../../../lib/sanity-client";
import renderModule from "../../../../lib/render-module";
import { toSuperHeroProps } from "../../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../../lib/utils";
import {
  BrewGuideCard,
  BrewGuidesPage as BrewGuidesPageSchema,
} from "../../../../lib/sanity-schema";
import { BaseProps, filters, keyof } from "../../../../lib/sanity/shared";
import {
  BrewGuidesPageSchemaWithGuide,
  toBrewGuideCardProps,
} from "../../../../lib/transformers/brew-guide-card";

interface PageData extends BaseProps {
  brewGuidesPage: BrewGuidesPageSchemaWithGuide;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);

  const brewGuidesPage = await sanityClient()
    .fetch<BrewGuidesPageSchemaWithGuide>(`*[
    ${filters.whereType("brewGuidesPage")} &&
    ${filters.excludeDrafts}
    ][0]{
    ...,
      ${keyof<BrewGuidesPageSchema>("guides")}[]{
        ...,
        ${keyof<BrewGuideCard>("brewGuide")}->
      },
    }`);

  return {
    props: {
      brewGuidesPage,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: BrewGuidesPageSchemaWithGuide) {
  if (!data.title) {
    throw Error("Brew Guides Page page must have a title.");
  }
}

const BrewGuidesPage: FunctionComponent<PageData> = ({
  brewGuidesPage,
  ...layoutWrapperProps
}) => {
  validate(brewGuidesPage);

  const { region, lang } = layoutWrapperProps;

  const brewGuideCards = brewGuidesPage.guides.map((s) =>
    toBrewGuideCardProps(s, lang, region),
  );

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        pageTitle: brewGuidesPage.title[lang],
        metaTags: brewGuidesPage.metaTags,
      }}
    >
      {brewGuidesPage.hero?.length > 0 && (
        <SuperHero {...toSuperHeroProps(brewGuidesPage.hero, region, lang)} />
      )}

      <BrewGuidesPreview brewGuideCards={brewGuideCards} />

      {brewGuidesPage.modules?.length > 0 &&
        brewGuidesPage.modules.map((m) => renderModule(m, region, lang))}
    </LayoutWrapper>
  );
};

export default BrewGuidesPage;
